import { CountryCode } from 'libphonenumber-js/types';
import {
  parsePhoneNumber,
  isPossiblePhoneNumber,
  formatPhoneNumberIntl,
  formatPhoneNumber as formatPhoneNumberNational,
} from 'react-phone-number-input';

import { PhoneCountry } from 'entities/inputs';

export enum CountryCallingCodes {
  US = '1',
  GB = '44',
}

// Placeholders must match the display format of the matching phoneFormatter.
export const PHONE_COUNTRIES: PhoneCountry[] = [
  { id: 1, code: 'US', placeholder: '+1 (000) 000-0000' },
  { id: 2, code: 'CA', placeholder: '+1 (000) 000-0000' },
  { id: 3, code: 'GB', placeholder: '+44 0000 000000' },
];

type PhoneFormatter = (value: string) => {
  display: string;
  e164: string;
};

const isValidPhoneLength = (phone: string, code: CountryCode) => {
  const length = phone.replace(/\D/g, '').length;

  // A UK number must have exactly 10 national digits after +44 (12 total).
  if (code === 'GB') {
    return length === 12;
  }

  return length == 11 || length == 12;
};

export const isValidPhoneNumber = (phone: string, code: CountryCode) => {
  return (
    !phone ||
    (isValidPhoneLength(phone, code) && isPossiblePhoneNumber(phone, code))
  );
};

export const getCountryCallingCodeAndNationalNumber = (phone: string) => {
  const parsedPhone = parsePhoneNumber(phone);

  return {
    countryCallingCode: `+${parsedPhone?.countryCallingCode}`,
    nationalNumber: parsedPhone?.nationalNumber,
  };
};

// Extracts the national digits from a stored value, dropping whatever
// country calling code it carries. This lets a number entered for one
// country be re-formatted for another without the old calling code
// leaking into the national part (e.g. US +14232445493 -> 4232445493).
export const getNationalDigits = (phone: string): string => {
  if (!phone) return '';

  const parsedPhone = parsePhoneNumber(phone);

  return parsedPhone?.nationalNumber
    ? String(parsedPhone.nationalNumber)
    : phone.replace(/\D/g, '');
};

// Formats a stored E.164 phone number for display. +1 numbers (US/CA) get
// the US format, everything else gets the international format from
// libphonenumber. Numbers that can't be parsed are returned unchanged.
//   formatPhoneNumber('+14232445493')   -> +1 (423) 244-5493
//   formatPhoneNumber('+447911123456')  -> +44 7911 123456
//   formatPhoneNumber('+448006525629')  -> +44 800 652 5629
export const formatPhoneNumber = (phoneNumber: string) => {
  const parsedPhone = parsePhoneNumber(phoneNumber);

  if (!parsedPhone) {
    return phoneNumber;
  }

  if (parsedPhone.countryCallingCode === CountryCallingCodes.US) {
    return `+${CountryCallingCodes.US} ${formatPhoneNumberNational(
      phoneNumber,
    )}`;
  }

  return formatPhoneNumberIntl(phoneNumber) || phoneNumber;
};

export const phoneFormatters: Partial<Record<CountryCode, PhoneFormatter>> = {
  US: (value: string) => {
    if (!value) return { display: '', e164: '' };

    const hasPlus = value.trim().startsWith('+');

    let digits = value.replace(/\D/g, '').slice(0, 11);

    if (hasPlus && digits.length === 0) {
      return { display: '+', e164: '' };
    }

    if (digits === '1') {
      return {
        display: `+${CountryCallingCodes.US}`,
        e164: `+${CountryCallingCodes.US}`,
      };
    }

    let display = '';

    if (digits.startsWith('1')) {
      digits = digits.slice(1);
      display = `+${CountryCallingCodes.US}`;
    } else if (digits.length > 0) {
      display = `+${CountryCallingCodes.US}`;
    }

    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6, 10);

    if (area.length) display += ` (${area}`;
    if (area.length === 3 && digits.length > 3) display += ')';
    if (prefix.length) display += ` ${prefix}`;
    if (line.length) display += `-${line}`;

    const e164 = digits.length ? `+${CountryCallingCodes.US}${digits}` : '';

    return { display, e164 };
  },
  GB: (value: string) => {
    if (!value) return { display: '', e164: '' };

    const hasPlus = value.trim().startsWith('+');

    let digits = value.replace(/\D/g, '').slice(0, 12);

    if (hasPlus && digits.length === 0) {
      return { display: '+', e164: '' };
    }

    if (digits === '4') {
      return {
        display: '+4',
        e164: '+4',
      };
    }

    if (digits === CountryCallingCodes.GB) {
      return {
        display: `+${CountryCallingCodes.GB}`,
        e164: `+${CountryCallingCodes.GB}`,
      };
    }

    let display = '';

    if (digits.startsWith(CountryCallingCodes.GB)) {
      digits = digits.slice(2);
      display = `+${CountryCallingCodes.GB}`;
    } else if (digits.length > 0) {
      display = `+${CountryCallingCodes.GB}`;
    }

    if (digits.startsWith('0')) {
      digits = digits.slice(1);

      if (!digits.length) {
        return {
          display: `+${CountryCallingCodes.GB}`,
          e164: `+${CountryCallingCodes.GB}`,
        };
      }
    }

    const part1 = digits.slice(0, 4);
    const part2 = digits.slice(4, 10);

    if (part1.length) display += ` ${part1}`;
    if (part2.length) display += ` ${part2}`;

    const e164 = digits.length ? `+${CountryCallingCodes.GB}${digits}` : '';

    return { display, e164 };
  },
};

phoneFormatters.CA = phoneFormatters.US;

export const defaultFormatter: PhoneFormatter = (value) => {
  const digits = value.replace(/\D/g, '');

  return {
    display: value,
    e164: digits ? `+${digits}` : '',
  };
};
